<template>
  <v-dialog v-model="dialog" max-width="800px">
    <v-card>
      <v-card-title class="primary white--text">
        <v-icon left color="white">mdi-bell-cog</v-icon>
        Configurar Alertas - {{ receptorName }}
        <v-spacer></v-spacer>
        <v-btn icon @click="closeDialog" color="white">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>

      <v-card-text class="pt-4">
        <v-alert type="info" text dense class="mb-4">
          Selecione quais alertas este receptor deve receber via WhatsApp
        </v-alert>

        <v-row class="mb-4">
          <v-col cols="12">
            <v-btn small outlined color="primary" @click="selectAll" class="mr-2">
              <v-icon left small>mdi-checkbox-marked</v-icon>
              Selecionar Todos
            </v-btn>
            <v-btn small outlined color="error" @click="deselectAll">
              <v-icon left small>mdi-checkbox-blank-outline</v-icon>
              Desmarcar Todos
            </v-btn>
          </v-col>
        </v-row>

        <v-divider class="mb-4"></v-divider>

        <!-- Categorias de notificações -->
        <v-expansion-panels v-model="expandedPanels" multiple>
          <v-expansion-panel
            v-for="category in categories"
            :key="category.key"
          >
            <v-expansion-panel-header>
              <div class="d-flex align-center">
                <v-icon :color="getCategoryColor(category.key)" class="mr-3">
                  {{ getCategoryIcon(category.key) }}
                </v-icon>
                <div>
                  <div class="font-weight-bold">{{ category.label }}</div>
                  <div class="text-caption grey--text">
                    {{ getSelectedCount(category.key) }} de {{ category.statuses.length }} selecionados
                  </div>
                </div>
              </div>
            </v-expansion-panel-header>

            <v-expansion-panel-content>
              <v-checkbox
                v-for="status in category.statuses"
                :key="status.code"
                v-model="selectedStatuses"
                :value="status.code"
                :label="status.label"
                dense
                hide-details
                class="mt-2"
              ></v-checkbox>
            </v-expansion-panel-content>
          </v-expansion-panel>
        </v-expansion-panels>

        <!-- Resumo de seleção -->
        <v-alert
          type="success"
          text
          dense
          class="mt-4"
          v-if="selectedStatuses.length > 0"
        >
          <strong>{{ selectedStatuses.length }}</strong> tipos de alertas selecionados
        </v-alert>

        <v-alert
          type="warning"
          text
          dense
          class="mt-4"
          v-if="selectedStatuses.length === 0"
        >
          Nenhum alerta selecionado. Este receptor não receberá notificações.
        </v-alert>
      </v-card-text>

      <v-divider></v-divider>

      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn text @click="closeDialog">
          Cancelar
        </v-btn>
        <v-btn
          color="primary"
          @click="saveConfiguration"
          :loading="saving"
        >
          <v-icon left>mdi-content-save</v-icon>
          Salvar Configuração
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import {
  NOTIFICATION_CATEGORIES,
  NOTIFICATION_CATEGORY_LABELS,
  getNotificationStatusesByCategory,
  getCategoryStatusCodes,
} from '@/pages/Projects/Utils/StatusConstants';
import { showConfirmDialogAlert } from '../../utils/DialogAlertHelper';

export default {
  name: 'NotificationReceptorConfig',
  props: {
    /**
     * Controla a visibilidade do dialog
     */
    value: {
      type: Boolean,
      required: true,
    },
    /**
     * ID do receptor sendo configurado
     */
    receptorId: {
      type: String,
      required: true,
    },
    /**
     * Nome do receptor para exibição
     */
    receptorName: {
      type: String,
      required: true,
    },
    /**
     * Array de códigos de status já selecionados
     */
    currentStatuses: {
      type: Array,
      default: () => [],
    },
  },

  data() {
    return {
      dialog: this.value,
      selectedStatuses: [...this.currentStatuses],
      expandedPanels: [0], // Primeira categoria expandida por padrão
      saving: false,
      dialogAlertOpen: false,
    };
  },

  computed: {
    /**
     * Retorna todas as categorias disponíveis com seus status
     */
    categories() {
      return Object.values(NOTIFICATION_CATEGORIES).map(categoryKey => ({
        key: categoryKey,
        label: NOTIFICATION_CATEGORY_LABELS[categoryKey],
        statuses: getNotificationStatusesByCategory(categoryKey),
      }));
    },
  },

  watch: {
    value(val) {
      this.dialog = val;
      if (val) {
        this.selectedStatuses = [...this.currentStatuses];
      }
    },
    dialog(val) {
      this.$emit('input', val);
    },
    currentStatuses: {
      handler(newStatuses) {
        this.selectedStatuses = [...newStatuses];
      },
      deep: true,
    },
  },

  methods: {
    handleSaveDialogAlert() {

      this.selectedStatuses = this.categories.flatMap(category =>
        category.statuses.map(s => s.code)
      );

    },

    handleCloseDialogAlert() {
      return false;
    },
    /**
     * Fecha o dialog sem salvar
     */
    closeDialog() {
      this.dialog = false;
      this.$emit('close', { 
        receptorId: this.receptorId, 
        statuses: [...this.currentStatuses], 
      });
    },

    /**
     * Seleciona todos os alertas de todas as categorias
     */
    selectAll() {
      showConfirmDialogAlert("Você tem certeza que deseja selecionar todos os alertas?", this.handleSaveDialogAlert)
    },

    /**
     * Desmarca todos os alertas
     */
    deselectAll() {
      this.selectedStatuses = [];
    },

    /**
     * Retorna quantos status estão selecionados em uma categoria
     */
    getSelectedCount(categoryKey) {
      const categoryCodes = getCategoryStatusCodes(categoryKey);
      return this.selectedStatuses.filter(code =>
        categoryCodes.includes(code)
      ).length;
    },

    /**
     * Retorna a cor da categoria (pode ser customizado)
     */
    getCategoryColor(categoryKey) {
      const colorMap = {
        [NOTIFICATION_CATEGORIES.WATERSCAN]: 'blue',
        [NOTIFICATION_CATEGORIES.APPROVAL]: 'green',
        [NOTIFICATION_CATEGORIES.CLOSING]: 'orange',
        [NOTIFICATION_CATEGORIES.CONTRACTS]: 'purple',
        [NOTIFICATION_CATEGORIES.MATERIALS]: 'teal',
        [NOTIFICATION_CATEGORIES.INSTALLATION]: 'indigo',
      };
      return colorMap[categoryKey] || 'grey';
    },

    /**
     * Retorna o ícone da categoria
     */
    getCategoryIcon(categoryKey) {
      const iconMap = {
        [NOTIFICATION_CATEGORIES.WATERSCAN]: 'mdi-water',
        [NOTIFICATION_CATEGORIES.APPROVAL]: 'mdi-check-circle',
        [NOTIFICATION_CATEGORIES.CLOSING]: 'mdi-handshake',
        [NOTIFICATION_CATEGORIES.CONTRACTS]: 'mdi-file-document',
        [NOTIFICATION_CATEGORIES.MATERIALS]: 'mdi-package-variant',
        [NOTIFICATION_CATEGORIES.INSTALLATION]: 'mdi-tools',
      };
      return iconMap[categoryKey] || 'mdi-bell';
    },

    /**
     * Salva a configuração e emite evento para o componente pai
     */
    async saveConfiguration() {
      this.saving = true;

      this.$emit('save', {
        receptorId: this.receptorId,
        statuses: [...this.selectedStatuses],
      });

      this.saving = false;
      this.closeDialog();
    },
  },
};
</script>

<style scoped>
.v-expansion-panel-header {
  min-height: 64px;
}

.v-expansion-panel-content >>> .v-expansion-panel-content__wrap {
  padding-left: 48px;
}
</style>
